// axiosInstance.ts
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { logoutUser } from "slices/thunk";

const baseURL = process.env.REACT_APP_BACKEND_BASE_URL;

// Create main Axios instance
const axiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create a separate instance for refreshing token
const refreshAxios = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token setup
const token = global?.localStorage?.getItem("authToken") || null;
if (token) {
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
}

let isRefreshing = false;
let failedRequestsQueue: {
  resolve: (value?: AxiosResponse<any>) => void;
  reject: (error: any) => void;
  config: AxiosRequestConfig;
}[] = [];

const setAuthorization = (token: string | null) => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const response = await refreshAxios.post("/api/user/refresh-token");
    const newToken = response.data?.data?.accessToken;

    if (newToken) {
      global?.localStorage?.setItem("authToken", newToken);
      setAuthorization(newToken);
      return newToken;
    } else {
      throw new Error("No access token in response");
    }
  } catch (error: any) {
    if (error.response?.status === 403) {
      global?.localStorage?.removeItem("authToken");
      logoutUser();
      setAuthorization(null);
      window.location.href = "/login";
    }
    return null;
  }
};

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response.data || response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    if (error.response?.status === 403 && !originalRequest._retry) {
      if (!isRefreshing) {
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${newToken}`,
            };

            failedRequestsQueue.forEach(async (req) => {
              req.config.headers = {
                ...req.config.headers,
                Authorization: `Bearer ${newToken}`,
              };
              req.resolve(await axiosInstance(req.config));
            });
            failedRequestsQueue = [];

            return axiosInstance(originalRequest);
          } else {
            failedRequestsQueue.forEach((req) => req.reject(error));
            failedRequestsQueue = [];
            throw error;
          }
        } catch (err) {
          failedRequestsQueue.forEach((req) => req.reject(err));
          failedRequestsQueue = [];
          throw err;
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({
            resolve,
            reject,
            config: originalRequest,
          });
        });
      }
    }

    const message = error.message || "An error occurred";
    return Promise.reject(message);
  }
);

class APIClient {
  get = (url: string, params?: any) => {
    const config: AxiosRequestConfig = {};
    if (params) config.params = params;
    return axiosInstance.get(url, config);
  };

  create = (url: string, data: any) => {
    const config: AxiosRequestConfig = this.getFormDataConfig(data);
    return axiosInstance.post(url, data, config);
  };

  post = (url: string, data?: any) => {
    const config: AxiosRequestConfig = this.getFormDataConfig(data);
    return axiosInstance.post(url, data, config);
  };

  update = (url: string, data: any) => {
    const config: AxiosRequestConfig = this.getFormDataConfig(data);
    return axiosInstance.patch(url, data, config);
  };

  put = (url: string, data: any) => {
    const config: AxiosRequestConfig = this.getFormDataConfig(data);
    return axiosInstance.put(url, data, config);
  };

  delete = (url: string, config?: any) => axiosInstance.delete(url, config);

  private getFormDataConfig(data: any): AxiosRequestConfig {
    const config: AxiosRequestConfig = {};
    if (data instanceof FormData) {
      config.headers = {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      };
    }
    return config;
  }
}

export { APIClient, setAuthorization };
